import React, { useMemo } from 'react';
import { Box, Flex, Skeleton, Stack } from '@chakra-ui/react';
import { uniViewApplicantTaskGroups } from 'data';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { CustomHeading, Finger } from 'shared-components';
import { Application, FingerStatus, IApplyUniConfig } from 'types';
import { ApplicationState } from '../../../../store';
import { getApplicationTaskGroups, getRouteStatusLoading, normalizeTaskRoute } from '../../../../store/apply/selectors';
import ApplicantHeader from '../../../molecules/applicantHeader';

interface Task {
  route: string;
  label: string;
  docCount?: number;
}

interface TaskGroup {
  label: string;
  tasks: Task[];
}

export default function Sections(): React.ReactElement {
  const { application } = useSelector((state: ApplicationState) => state.apply);
  const { applyConfig } = useSelector((state: ApplicationState) => state.applyConfig);
  // Get dynamic task groups from route status API
  const routeStatusTaskGroups = useSelector(getApplicationTaskGroups);
  const isRouteStatusLoading = useSelector(getRouteStatusLoading);

  // Use dynamic task groups from route status API if available, fallback to static config
  const taskGroups = useMemo(() => {
    if (routeStatusTaskGroups.length > 0) {
      return routeStatusTaskGroups.map(group => ({
        label: group.label,
        tasks: group.tasks.map(task => {
          const route = normalizeTaskRoute(task.route);
          return {
            label: task.label,
            route,
            docCount: calculateFallbackDocumentCount(route, application),
          };
        }),
      }));
    }
    return getUniViewApplicantTaskGroupsWithDocs(application, applyConfig);
  }, [routeStatusTaskGroups, application, applyConfig]);

  return (
    <>
      <ApplicantHeader>
        <Box mb={8}>
          <Flex mb={2} flexWrap="wrap" rowGap={4} columnGap={3}>
            <CustomHeading data-selector="sectionsHeader-name" level="h1" flexGrow={1} mb={0} wordBreak="break-word">
              Application
            </CustomHeading>
          </Flex>
        </Box>
      </ApplicantHeader>

      {isRouteStatusLoading ? (
        <Stack spacing={6}>
          {[1, 2, 3, 4].map(i => (
            <Box key={i}>
              <Skeleton height="24px" width="200px" mb={3} />
              <Stack spacing={2}>
                <Skeleton height="48px" />
                <Skeleton height="48px" />
                <Skeleton height="48px" />
              </Stack>
            </Box>
          ))}
        </Stack>
      ) : (
        taskGroups.map(taskGroup => (
          <Box key={taskGroup.label} mb={10}>
            <CustomHeading level="h2" mb={2} fontWeight="medium" fontSize="xl">
              {taskGroup.label}
            </CustomHeading>

            {taskGroup.tasks.map(task => (
              <Finger
                id={task.route}
                key={task.route}
                status={FingerStatus.None}
                label={task.label}
                linkAs={RouterLink}
                to={`..${task.route}`}
                noneLabel={task.docCount}
              />
            ))}
          </Box>
        ))
      )}
    </>
  );
}

function calculateFallbackDocumentCount(route: string, application: Application): number {
  switch (route) {
    case '/identity':
      return (application.passportDocument?.s3Keys?.length || 0) + (application.idDocument?.s3Keys?.length || 0);
    case '/education-history': {
      // Count files from studyHistory (highest + all previous studies)
      let count = 0;

      // Count files from highest study
      if (application.studyHistory?.highest?.academicTranscriptDocument?.s3Keys) {
        count += application.studyHistory.highest.academicTranscriptDocument.s3Keys.length;
      }

      // Count files from all previous studies
      if (application.studyHistory?.previous) {
        for (const study of Object.values(application.studyHistory.previous)) {
          if (study.academicTranscriptDocument?.s3Keys) {
            count += study.academicTranscriptDocument.s3Keys.length;
          }
        }
      }

      return count;
    }
    case '/english-language':
      return application.englishTestDocument?.s3Keys?.length || 0;
    case '/employment':
      return application.cvDocument?.s3Keys?.length || 0;
    case '/references':
      return application.referenceLetterDocument?.s3Keys?.length || 0;
    case '/personal-statement':
      return application.personalStatementDocument?.s3Keys?.length || 0;
    case '/research-proposal':
      return application.researchProposalDocument?.s3Keys?.length || 0;
    default:
      return 0;
  }
}

function shouldIncludeTask(task: { route: string }, applyConfig?: IApplyUniConfig): boolean {
  if (task.route === '/declarations' && applyConfig?.disableDeclarationTask) {
    return false;
  }
  return true;
}

export function getUniViewApplicantTaskGroupsWithDocs(
  application: Application,
  applyConfig?: IApplyUniConfig,
): TaskGroup[] {
  return uniViewApplicantTaskGroups.map(group => ({
    ...group,
    tasks: group.tasks
      .map(task => ({
        ...task,
        docCount: calculateFallbackDocumentCount(task.route, application),
      }))
      .filter(task => shouldIncludeTask(task, applyConfig)),
  }));
}
